import { Box, Button, Flex, Tab, TabList, Tabs, Text } from '@chakra-ui/react';
import { Card } from '@ui/components/Card';
import {
  DateRangeIndicator,
  useDateRangePickerModal,
} from '@ui/components/DateRangePicker';
import Features from '@ui/components/Features';
import { useDefaultAppBar } from '@ui/components/MobileAppBarProvider';
import { InDesktop, InMobile } from '@ui/components/MobileOrDesktop';
import Notification from '@ui/components/Notification';
import StatsChart from '@ui/components/StatsCharts/StatsChart';
import { PATH_STATS_STAKING, PATH_STATS_VNO } from '@ui/config/paths';
import { useTranslations } from '@ui/i18n';
import { MAX_W_FAQ_CARD } from '@ui/utils/constants';
import { replaceRouterWithoutRerender } from '@ui/utils/urlHistory';
import { isSameDay, sub } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { useScrollContainer } from 'react-indiana-drag-scroll';

import type { StatsChartPage } from './useStatsCharts';
import { useStatsCharts } from './useStatsCharts';
import { useStatsQA } from './useStatsQA';

function StatsContent({ defaultPage }: { defaultPage: StatsChartPage }) {
  useDefaultAppBar();
  const [page, setPage] = useState<StatsChartPage>(defaultPage);
  const qas = useStatsQA(page);
  const t = useTranslations();

  const presetDateRanges: {
    name: string;
    dateRange: Date[];
  }[] = useMemo(
    () => [
      {
        name: t('{num} Months', { num: 3 }),
        dateRange: [
          sub(new Date(), {
            months: 3,
          }),
          new Date(),
        ],
      },
      {
        name: t('{num} Months', { num: 6 }),
        dateRange: [
          sub(new Date(), {
            months: 6,
          }),
          new Date(),
        ],
      },
      {
        name: t('1 year'),
        dateRange: [
          sub(new Date(), {
            years: 1,
          }),
          new Date(),
        ],
      },
    ],
    [t],
  );

  // bg image from design is not perfect if there are charts, commenting it out for now
  // const bgImageProps = getBgImage('PATTERN_3');

  const today = new Date(
    Date.UTC(
      new Date().getUTCFullYear(),
      new Date().getUTCMonth(),
      new Date().getUTCDate(),
    ),
  );
  const threeMonthsAgo = new Date(today);
  threeMonthsAgo.setMonth(today.getMonth() - 3);

  const [startDate, setStartDate] = useState(threeMonthsAgo);
  const [endDate, setEndDate] = useState(today);

  const stats = useStatsCharts(startDate, endDate, page);

  const scrollContainer = useScrollContainer();

  const presetDateRangeActiveIndex = useMemo(() => {
    return presetDateRanges.findIndex(
      (item) =>
        isSameDay(startDate, item.dateRange[0]) &&
        isSameDay(endDate, item.dateRange[1]),
    );
  }, [endDate, startDate, presetDateRanges]);
  const isCustomDate = presetDateRangeActiveIndex < 0;

  return (
    <Box
      px={{ base: 4 }}
      bgAttachment="fixed"
      // bg image from design is not perfect if there are charts, commenting it out for now
      // bgPos="right 50px"
      // {...bgImageProps}
    >
      <Text
        as="h1"
        textStyle={{
          base: 'h3',
          desktop: 'h1',
        }}
        textAlign={{
          base: 'start',
          desktop: 'center',
        }}
      >
        {t('Veno Statistics')}
      </Text>

      <Features.statsStakingTab>
        <>
          <InMobile>
            <Tabs mt={4} mx={-4} index={page === 'vno' ? 0 : 1}>
              <TabList>
                <Tab
                  onClick={() => {
                    setPage('vno');
                    replaceRouterWithoutRerender(PATH_STATS_VNO);
                  }}
                >
                  VNO
                </Tab>
                <Tab
                  onClick={() => {
                    setPage('staking');
                    replaceRouterWithoutRerender(PATH_STATS_STAKING);
                  }}
                >
                  {t('Staking')}
                </Tab>
              </TabList>
            </Tabs>
          </InMobile>

          <InDesktop>
            <Flex
              justify="flex-start"
              gap={4}
              mt={{
                base: '16px',
                desktop: '32px',
              }}
            >
              <Card
                w="100px"
                m={0}
                whiteSpace="nowrap"
                variant="button"
                isActive={page === 'vno'}
                cursor="pointer"
                textStyle="h4Bold"
                color={page === 'vno' ? 'text-active' : 'text-inactive'}
                p="10px"
                textAlign="center"
                justifyContent="center"
                onClick={() => {
                  setPage('vno');
                  replaceRouterWithoutRerender(PATH_STATS_VNO);
                }}
              >
                VNO
              </Card>
              <Card
                w="100px"
                m={0}
                whiteSpace="nowrap"
                variant="button"
                isActive={page === 'staking'}
                cursor="pointer"
                textStyle="h4Bold"
                color={page === 'staking' ? 'text-active' : 'text-inactive'}
                p="10px"
                textAlign="center"
                justifyContent="center"
                onClick={() => {
                  setPage('staking');
                  replaceRouterWithoutRerender(PATH_STATS_STAKING);
                }}
              >
                {t('Staking')}
              </Card>
            </Flex>
          </InDesktop>
        </>
      </Features.statsStakingTab>

      <Flex
        mt={4}
        justifyContent={{
          desktop: 'center',
        }}
      >
        {t('Filter by')}:
        <DateRangeIndicator
          selectedDate={[startDate, endDate]}
          textTransform="lowercase"
          ml="4px"
        />
      </Flex>
      <Flex
        ref={scrollContainer.ref}
        className="no-scrollbar"
        gap="12px"
        justifyContent={{
          desktop: 'center',
        }}
        flexWrap="nowrap"
        mt="16px"
        overflowX="auto"
        mx="-16px"
        px="16px"
      >
        {presetDateRanges.map((item, index) => {
          const isActive = presetDateRangeActiveIndex === index;
          return (
            <Button
              key={index}
              flexShrink="0"
              variant={isActive ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => {
                setStartDate(item.dateRange[0]);
                setEndDate(item.dateRange[1]);
              }}
            >
              {item.name}
            </Button>
          );
        })}
        <Button
          variant={isCustomDate ? 'primary' : 'secondary'}
          flexShrink="0"
          size="sm"
          onClick={() => {
            useDateRangePickerModal.getState().onOpen({
              selectedDate: [startDate, endDate],
              onConfirm(selectedDate) {
                setStartDate(selectedDate[0]);
                setEndDate(selectedDate[1]);
              },
            });
          }}
        >
          {t('Custom')}
        </Button>
      </Flex>
      <Flex
        justify="space-between"
        gap={{ base: 4, desktop: 6 }}
        w="full"
        wrap="wrap"
        mt="52px"
      >
        {stats.map((s) => (
          <Box pb={4} key={s.title} w={{ base: 'full', desktop: '48%' }}>
            <StatsChart
              type={s.type}
              title={s.title}
              values={s.values}
              units={s.units}
              alternativeValues={s.alternativeValues}
            />
          </Box>
        ))}
      </Flex>
      <Text
        textAlign="center"
        pt="48px"
        textStyle={{ base: 'h3', desktop: 'h2' }}
        fontWeight="normal"
      >
        {t('Q&A')}
      </Text>
      <Flex mt={6} flexDir="column" gap={4} mx="auto" maxW={MAX_W_FAQ_CARD}>
        {qas.map((qa, i) => (
          <Notification key={`${i}${page}`} {...qa} />
        ))}
      </Flex>
    </Box>
  );
}

export default StatsContent;
